import { Component, OnInit } from '@angular/core';
import { ApiService } from 'src/app/services/api/api.service';
import { SignupService } from 'src/app/services/signup/signup.service';
import { ToastService } from 'src/app/services/toast/toast.service';
import { Router } from '@angular/router';
import { FormBuilder } from '@angular/forms';
import { AuthService } from 'src/app/services/auth/auth.service';
import {
  BreadcrumbService,
  BreadcrumbItem,
} from 'src/app/services/breadcrumb/breadcrumb.service';
import { DRAFTS_PAGE_URL, draftsTabUrl } from 'src/app/data/draft-tabs';
import {
  getDocumentTypeEventLabel,
  mapMinuteTypeEventOptions,
} from 'src/app/data/document-type-events';

@Component({
  selector: 'app-dashboard-drafts-versions',
  templateUrl: './dashboard-drafts-versions.component.html',
  styleUrls: ['./dashboard-drafts-versions.component.scss'],
})
export class DashboardDraftsVersionsComponent implements OnInit {
  permissionObj = {
    canAdd: false,
    canEdit: false,
    canChange: false,
    canFilter: false,
  };

  data: DraftTypesValues[] = [];
  eventOptions: SelectItem[] = [];

  filterForm = this.formBuilder.group({
    type_document: [null, []],
    event: [null, []],
    created_at_after: [null, []],
    created_at_before: [null, []],
    status: [null, []],
  });

  statusOptions: SelectItem[] = [
    { label: 'Ativo', value: 'true' },
    { label: 'Inativo', value: 'false' },
  ];

  loading = true;
  sortButtonExpanded = false;
  /**
   * Padrão de Filtros:
   * - hasFilters só deve ser true APÓS aplicar os filtros (método filterData())
   * - NÃO atualizar hasFilters no valueChanges do filterForm
   * - hasFilters deve ser atualizado apenas quando:
   *   1. filterData() executar com sucesso (após a requisição)
   *   2. resetForm() for chamado (define como false)
   */
  hasFilters = false;
  appliedFilters: Array<{ label: string; value: string }> = [];
  showFilterModal = false;
  offset: number = 0;
  count: number = 0;
  results_per_page = 0;

  // Propriedades para o warning modal
  showStatusModal = false;
  selectedVersion: any = null;
  isActive: boolean = false;

  constructor(
    private formBuilder: FormBuilder,
    private api: ApiService,
    private toast: ToastService,
    public signupService: SignupService,
    private router: Router,
    private authService: AuthService,
    private breadcrumbService: BreadcrumbService,
  ) {}

  ngOnInit(): void {
    this.authService.canViewPage(['de_can_view_types']);

    this.setPermissions();
    this.setBreadcrumb();
    void this.initData();
  }

  private async initData() {
    await this.fetchEvents();
    await this.getData();
  }

  async fetchEvents() {
    try {
      const response = await this.api.get({
        route: 'api/minute-type-configuration/events/',
        token: true,
      });

      this.eventOptions = mapMinuteTypeEventOptions(response);
    } catch (error) {
      console.error(error);
      this.toast.show(
        'error',
        'Erro',
        'Ocorreu um erro ao carregar os eventos.',
      );
    }
  }

  getEventLabel(event?: string | null): string {
    return getDocumentTypeEventLabel(event, this.eventOptions);
  }

  async getData(page: number = 1) {
    try {
      const filters = {};

      if (this.hasFilters) {
        const values = this.filterForm.value;
        Object.entries(values).forEach(([key, value]: any) => {
          if (value !== null && value !== '') {
            let newValue = value;
            if (key === 'created_at_after' || key === 'created_at_before') {
              newValue = value.split('/').reverse().join('-');
            }
            filters[key] = newValue;
          }
        });
      }

      if (filters['created_at_after'] && !filters['created_at_before']) {
        const atualDate = new Date().toISOString().split('T')[0];
        filters['created_at_before'] = atualDate;
      }

      if (filters['created_at_before'] && !filters['created_at_after']) {
        filters['created_at_after'] = '2000-01-01';
      }

      const { count, results, offset, results_per_page } = await this.api.get({
        route: 'api/document-type/',
        token: true,
        params: { page: page, ordering: this.getOrdering(), ...filters },
      });

      this.data = results;
      this.offset = offset;
      this.count = count;
      this.results_per_page = results_per_page;

      // Se houver filtros aplicados, gerar as tags
      if (this.hasFilters && Object.keys(filters).length > 0) {
        this.generateFilterTags(this.filterForm.value);
      } else {
        this.hasFilters = false;
        this.appliedFilters = [];
      }
    } catch (error) {
      console.error(error);
      this.toast.show('error', 'Erro', 'Ocorreu um erro ao carregar os dados.');
    }
    this.loading = false;
  }

  addDraft() {
    this.router.navigateByUrl('/app/drafts/types/new');
  }

  toggleDraftStatus(id: any) {
    const draftType = this.data.find((item) => item.id === id);
    this.selectedVersion = draftType;
    this.isActive = draftType.status;
    this.showStatusModal = true;
  }

  async confirmStatusChange() {
    try {
      await this.api.patch({
        route: `api/minute-type-configuration/${this.selectedVersion.id}/`,
        token: true,
        body: { status: !this.isActive },
      });

      this.loading = true;
      this.showStatusModal = false;
      this.getData();

      this.toast.show(
        'info',
        'Sucesso',
        `Tipo de documento ${
          this.isActive ? 'inativado' : 'ativado'
        } com sucesso.`,
      );
    } catch (error) {
      console.error(error);
      this.toast.show(
        'error',
        'Erro',
        `Ocorreu um erro ao alterar status do tipo de documento.`,
      );
    }
  }

  closeStatusModal() {
    this.showStatusModal = false;
    this.selectedVersion = null;
    this.isActive = false;
  }

  getModalTitle(): string {
    return this.isActive
      ? 'Inativar tipo de documento'
      : 'Ativar tipo de documento';
  }

  getModalMessage(): string {
    return `Deseja ${
      this.isActive ? 'inativar' : 'ativar'
    } esse tipo de documento? Essa ação irá ${
      this.isActive ? 'inativar' : 'ativar'
    } o tipo de documento.`;
  }

  getConfirmText(): string {
    return `Sim, ${this.isActive ? 'inativar' : 'ativar'} tipo de documento`;
  }

  editDraft(id: any) {
    this.router.navigateByUrl(`/app/drafts/types/${id}`);
  }

  redirectToDetails(id: any) {
    this.router.navigateByUrl(`/app/permissions/details/${id}`);
  }

  changePageData(page: number) {
    this.loading = true;
    this.getData(page);
  }

  async refreshData() {
    this.loading = true;
    await this.getData();
  }

  async toggleSortButton() {
    this.sortButtonExpanded = !this.sortButtonExpanded;
    this.loading = true;
    await this.getData(1);
  }

  getSortButtonLabel() {
    return this.sortButtonExpanded ? 'Mais antigos' : 'Mais recentes';
  }

  private getOrdering() {
    return this.sortButtonExpanded ? 'created_at' : '-created_at';
  }

  resetForm() {
    this.hasFilters = false;
    this.appliedFilters = [];
    this.filterForm.reset();
    this.showFilterModal = false;
    this.loading = true;
    this.getData();
  }

  generateFilterTags(formValues: any) {
    this.appliedFilters = [];

    const fieldLabels: { [key: string]: string } = {
      type_document: 'Nome',
      event: 'Evento',
      status: 'Status',
      created_at_after: 'Data de início',
      created_at_before: 'Data final',
    };

    Object.entries(formValues).forEach(([key, value]: any) => {
      if (value !== null && value !== '' && value !== undefined) {
        let displayValue = value;

        // Tratamento especial para status
        if (key === 'status') {
          const statusOption = this.statusOptions.find(
            (s) => s.value === value,
          );
          displayValue = statusOption ? statusOption.label : value;
        }

        if (key === 'event') {
          displayValue = this.getEventLabel(value);
        }

        // Tratamento especial para datas
        if (key === 'created_at_after' || key === 'created_at_before') {
          displayValue = value;
        }

        if (fieldLabels[key]) {
          // Combinar label e valor no formato "label: valor"
          const combinedLabel = `${fieldLabels[key]}: ${String(displayValue)}`;
          this.appliedFilters.push({
            label: combinedLabel,
            value: String(displayValue),
          });
        }
      }
    });
  }

  async filterData() {
    this.loading = true;

    try {
      const values = this.filterForm.value;

      const filters = {};

      Object.entries(values).forEach(([key, value]: any) => {
        if (value !== null && value !== '') {
          let newValue = value;
          if (key === 'created_at_after' || key === 'created_at_before') {
            newValue = value.split('/').reverse().join('-');
          }
          filters[key] = newValue;
        }
      });

      if (filters['created_at_after'] && !filters['created_at_before']) {
        const atualDate = new Date().toISOString().split('T')[0];
        filters['created_at_before'] = atualDate;
      }

      if (filters['created_at_before'] && !filters['created_at_after']) {
        filters['created_at_after'] = '2000-01-01';
      }

      const { count, results, offset, results_per_page } = await this.api.get({
        route: 'api/document-type/',
        token: true,
        params: {
          page: 1,
          ordering: this.getOrdering(),
          ...filters,
        },
      });

      this.data = results;

      // ✅ IMPORTANTE: hasFilters só é atualizado após aplicar os filtros com sucesso
      this.hasFilters = Object.keys(filters).length > 0;

      // ✅ Gerar as tags dos filtros aplicados
      this.generateFilterTags(this.filterForm.value);

      this.count = count;
      this.offset = offset;
      this.results_per_page = results_per_page;
    } catch (error) {
      console.error(error);
      this.toast.show('error', 'Erro', 'Ocorreu um erro ao carregar os dados.');
    }
    this.loading = false;
  }

  setPermissions() {
    const permissions = Object.keys(this.permissionObj).map(
      (key) => `de_can_${key.split('can').join('').toLowerCase()}_types`,
    );

    permissions.forEach((permission, idx) => {
      this.permissionObj[Object.keys(this.permissionObj)[idx]] =
        this.authService.verifyPermission(permission);
    });
  }

  setBreadcrumb(): void {
    const canViewDrafts =
      this.authService.verifyPermission('de_can_view_drafts');
    const breadcrumbs: BreadcrumbItem[] = [
      {
        label: 'Minutas',
        url: DRAFTS_PAGE_URL,
        clickable: canViewDrafts,
      },
      {
        label: 'Tipos',
        url: draftsTabUrl('types'),
        clickable: false,
      },
    ];
    this.breadcrumbService.setBreadcrumbs(breadcrumbs);
  }
}
